export interface ClockSource {
  wallNow: () => number;
  monotonicNow: () => number;
}

const DEFAULT_SOURCE: ClockSource = {
  wallNow: () => Date.now(),
  monotonicNow: () => performance.now(),
};

export class SynchronizedServerClock {
  private serverAnchorMs = 0;
  private monotonicAnchorMs = 0;
  private lastServerSampleMs = 0;
  private lastAdjustedSampleMs = 0;
  private lastReturnedMs = 0;
  private lastClientTickMs = 0;
  private synchronized = false;

  constructor(private readonly source: ClockSource = DEFAULT_SOURCE) {}

  synchronize(serverNowMs: number, roundTripMs = 0): boolean {
    if (!Number.isFinite(serverNowMs) || serverNowMs <= 0) return false;
    const boundedRoundTrip = Number.isFinite(roundTripMs)
      ? Math.max(0, Math.min(10_000, roundTripMs))
      : 0;
    const adjustedSample = serverNowMs + boundedRoundTrip / 2;
    if (
      serverNowMs < this.lastServerSampleMs ||
      (serverNowMs === this.lastServerSampleMs && adjustedSample <= this.lastAdjustedSampleMs)
    )
      return false;
    this.serverAnchorMs = adjustedSample;
    this.monotonicAnchorMs = this.source.monotonicNow();
    this.lastServerSampleMs = serverNowMs;
    this.lastAdjustedSampleMs = adjustedSample;
    this.synchronized = true;
    return true;
  }

  now(): number {
    const candidate = this.synchronized
      ? this.serverAnchorMs + Math.max(0, this.source.monotonicNow() - this.monotonicAnchorMs)
      : this.source.wallNow();
    this.lastReturnedMs = Math.max(this.lastReturnedMs, candidate);
    return Math.round(this.lastReturnedMs);
  }

  nextClientTick(): number {
    this.lastClientTickMs = Math.max(this.lastClientTickMs, this.now());
    return this.lastClientTickMs;
  }
}
